import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Search, Phone, Mail, Info, User, Instagram, Facebook } from "lucide-react";
import { Reveal, Marquee, useUniverseSpaces, useSettings, DEFAULT_HERO, DEFAULT_CONTACT } from "@/components/shared";
import SearchOverlay from "@/components/layout/SearchOverlay";
import ChatWidget from "@/components/layout/ChatWidget";
import VipBanner from "@/components/layout/VipBanner";
import CustomSections from "@/components/layout/CustomSections";
import FreeCanvasRender from "@/components/layout/FreeCanvasRender";

const lineReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 1, delay: 0.25 + i * 0.16, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: 0.7 + i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Univers() {
  const settings = useSettings();
  const spaces = useUniverseSpaces();
  const [searchOpen, setSearchOpen] = useState(false);
  const heroImage = settings?.hero_image || DEFAULT_HERO;
  const four = spaces.length >= 4;
  const contact = { ...DEFAULT_CONTACT, ...(settings?.contact || {}) };
  const SHORT_LABELS = { palais: "Palais", comptoir: "Comptoir", services: "Services" };

  return (
    <div className="min-h-screen bg-ink" data-testid="univers-page">
      {/* Header portail */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        {/* Rangée utilitaire — desktop */}
        <div className="hidden h-9 items-center justify-between border-b border-white/5 px-10 lg:flex" data-testid="univers-utility-bar">
          <div className="flex items-center gap-6 text-[11px] tracking-wide text-ivory/45">
            <a href={`tel:${(contact.whatsapp || "").replace(/\s/g, "")}`} className="flex items-center gap-2 transition-colors hover:text-gold" data-testid="univers-phone">
              <Phone className="h-3.5 w-3.5 text-gold/70" strokeWidth={1.5} />
              {contact.phone_display}
            </a>
            <span className="text-ivory/35">
              {contact.address_street}, {contact.address_city}
            </span>
          </div>
          <div className="flex items-center gap-5 text-[11px] font-medium uppercase tracking-[0.12em]">
            <Link to="/notre-histoire" className="flex items-center gap-1.5 text-ivory/55 transition-colors hover:text-gold" data-testid="univers-about-link">
              <Info className="h-3.5 w-3.5" strokeWidth={1.5} />
              À propos
            </Link>
            <Link to="/contact" className="flex items-center gap-1.5 text-ivory/55 transition-colors hover:text-gold" data-testid="univers-contact-link">
              <Mail className="h-3.5 w-3.5" strokeWidth={1.5} />
              Contact
            </Link>
            <Link to="/mon-espace" className="flex items-center gap-1.5 text-ivory/55 transition-colors hover:text-gold" data-testid="univers-account-link">
              <User className="h-3.5 w-3.5" strokeWidth={1.5} />
              Compte
            </Link>
            <span className="h-3.5 w-px bg-white/10" />
            <a
              href={settings?.instagram_url || "https://instagram.com"}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="univers-instagram"
              className="text-ivory/55 transition-colors hover:text-gold"
            >
              <Instagram className="h-4 w-4" strokeWidth={1.5} />
            </a>
            <a
              href={settings?.facebook_url || "https://facebook.com"}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              data-testid="univers-facebook"
              className="text-ivory/55 transition-colors hover:text-gold"
            >
              <Facebook className="h-4 w-4" strokeWidth={1.5} />
            </a>
          </div>
        </div>

        {/* Rangée principale : marque + recherche bien visible */}
        <div className="relative flex h-20 w-full items-center justify-between px-5 md:px-10 lg:justify-center">
          <Link to="/" className="text-sm font-bold tracking-[0.28em] text-ivory md:text-base lg:absolute lg:left-10" data-testid="univers-brand">
            UNIVERS&nbsp;MAASAÏ
          </Link>
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="univers-search-field"
            className="mx-auto hidden h-12 w-full max-w-xl items-center gap-3 rounded-full border border-white/15 bg-white/[0.04] pl-5 pr-2 text-sm text-ivory/40 transition-colors hover:border-gold/40 hover:text-ivory/60 md:flex"
          >
            <span className="flex-1 text-left">Rechercher un plat, une prestation, un produit…</span>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-copper text-ivory">
              <Search className="h-4 w-4" strokeWidth={1.5} />
            </span>
          </button>
          <button
            onClick={() => setSearchOpen(true)}
            aria-label="Rechercher"
            data-testid="univers-search-button"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-gold hover:text-gold md:hidden"
          >
            <Search className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Rangée activités — centrée, liens mis en valeur */}
        <nav className="flex h-12 items-center justify-center gap-6 border-t border-white/5 md:h-16 md:gap-14" data-testid="univers-nav">
          {spaces.map((space) => (
            <Link
              key={space.id}
              to={space.path}
              data-testid={`univers-nav-${space.id}`}
              className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ivory/65 transition-colors hover:text-copper sm:text-xs md:text-xl md:tracking-[0.16em]"
            >
              <span className="hidden sm:inline">{space.name}</span>
              <span className="sm:hidden">{SHORT_LABELS[space.id] || space.name}</span>
            </Link>
          ))}
        </nav>
      </header>

      {/* Hero */}
      <section className="relative flex min-h-screen items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Univers Maasaï" className="hero-zoom h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-ink to-transparent" />

        <div className="relative z-10 px-6 text-center">
          <motion.p
            custom={0}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="overline mb-8"
            data-testid="univers-overline"
          >
            Restaurant · Boutique · Services — Dijon
          </motion.p>
          <h1 className="whitespace-nowrap text-[clamp(1.9rem,6.7vw,6.3rem)] font-bold leading-[0.95] tracking-tight text-copper">
            <span className="block overflow-hidden pb-1">
              <motion.span
                custom={0}
                variants={lineReveal}
                initial="hidden"
                animate="visible"
                className="block"
                data-testid="univers-title-line-0"
              >
                UNIVERS&nbsp;MAASAÏ
              </motion.span>
            </span>
          </h1>
          <motion.p
            custom={1}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="mx-auto mt-8 max-w-xl text-sm font-medium uppercase tracking-[0.25em] text-ivory/70 md:text-base"
            data-testid="univers-subtitle"
          >
            {spaces.length} activités, une même exigence
          </motion.p>
        </div>
      </section>

      {/* À la une — soirée VIP */}
      <VipBanner />

      {/* Les trois espaces */}
      <section className="px-6 py-28 md:px-12 md:py-36">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <div className="text-center">
              <p className="overline mb-6">Nos activités</p>
              <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-ivory sm:text-4xl lg:text-5xl">
                Choisissez votre expérience
              </h2>
            </div>
          </Reveal>
          <div className={`mt-16 grid gap-8 ${four ? "md:grid-cols-2 xl:grid-cols-4" : "lg:grid-cols-3"}`}>
            {spaces.map((space, i) => (
              <Reveal key={space.id} delay={i * 0.12}>
                <Link
                  to={space.path}
                  className="group flex h-full flex-col rounded-lg border border-white/10 bg-coal/60 p-10 transition-colors duration-300 hover:border-gold/40"
                  data-el="univers.card"
                  data-testid={`univers-card-${space.id}`}
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-lg border border-gold/30 text-gold">
                    <space.icon className="h-7 w-7" strokeWidth={1.25} />
                  </span>
                  <span className="mt-8 text-xs font-bold tracking-[0.3em] text-copper">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-3 text-2xl font-bold tracking-tight text-ivory transition-colors group-hover:text-gold">
                    {space.name}
                  </h3>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-ivory/50">
                    {space.tagline}
                  </p>
                  <span className="mt-8 inline-flex items-center gap-3 text-[12px] font-semibold uppercase tracking-[0.18em] text-gold">
                    Découvrir
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee
        items={[
          "Univers Maasaï",
          ...spaces.map((s) => s.name),
          "Dijon",
        ]}
      />

      <CustomSections page="univers" />
      <FreeCanvasRender page="univers" />

      {/* Footer portail */}
      <footer className="border-t border-white/10 px-6 py-12 md:px-12">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-6 text-center">
          <p className="text-sm font-bold tracking-[0.28em] text-copper">UNIVERS MAASAÏ</p>
          <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2" data-testid="univers-legal-links">
            {[
              { to: "/mentions-legales", label: "Mentions légales" },
              { to: "/politique-de-confidentialite", label: "Confidentialité" },
              { to: "/cgv", label: "CGV" },
              { to: "/politique-de-cookies", label: "Cookies" },
            ].map((link) => (
              <Link key={link.to} to={link.to} className="text-xs text-ivory/40 transition-colors hover:text-gold">
                {link.label}
              </Link>
            ))}
          </nav>
          <p className="text-[11px] tracking-wide text-ivory/30">
            © 2026 Univers Maasaï – Tous droits réservés
          </p>
        </div>
      </footer>
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <ChatWidget />
    </div>
  );
}
